import React, { useEffect, useMemo, useState } from 'react';
import { DateShortInfo, EventShortInfo } from '../../ReportsClasses';
import { Groupper, Header, Placeholder, Stat, Table } from 'react-frontier';
import { useTranslation } from 'react-i18next';
import { Label } from '@arema/components';
import { addCommas, relativeTime } from '@arema/components/Util';
import moment from 'moment';
import API from '../../ReportsAPI';

interface DateStatsDashboardProps{
	event_id: number,
	event: EventShortInfo,
	date: DateShortInfo,
}

interface EventDateSummary{
	date: {
		date_id: number,
		date: number,
		published: boolean,
		refundable: boolean,
		available_web: boolean,
		available_pdv: boolean,
	},
	summary: {
		date?: number,
		money: number,
		tickets: number,
		average_sale: number,
	}
}

var DateStatsDashboard = (props: DateStatsDashboardProps)=>{
	var { t } = useTranslation();
	var [loadError, setLoadError] = useState<string>(null);
	var [data, setData] = useState<EventDateSummary>(null);

	useEffect(()=>{
		if(!props.date || (data && props.date.date_id!==data.date.date_id)){
			setData(null);
		}
		if(!props.date) return;
		API.getDateSummary(props.date.date_id).then(res=>{
			if(res.error) return setLoadError(res.message);
			setData(res.data);
		}).catch(err=>{
			setLoadError(t('common.unexpected_code', { code: 'LST-DTSTDSH-1' }));
		});
	}, [props.date]);
	
	var past = useMemo(()=>data && moment().unix()>data.date.date, [data]);
	var date = useMemo(()=>moment.unix(data?.date.date || props.date?.date), [props.date, data]);
	var timeUntil = useMemo(()=>relativeTime(data?.date.date || props.date?.date), [props.date, data]);

	if(loadError) return <Header text={t('common.error')} subtext={loadError} iconName='face-frown-open' />
	if(!props.date) return <Header loading text={t('date_dashboard.loading')} />

	return <div className="ar event-stats-date-dashboard">
		<Table
			details
			striped
			title={t('date_dashboard.table.header')}
			style={{ maxWidth: 500, margin: 'auto', marginTop: 15 }}
			data={[
				[t('date_dashboard.table.id'), props.date.date_id],
				[t('date_dashboard.table.date'), <>
					{date.format('DD/MMM/YY HH:mm')}
					<div className="meta">{timeUntil}</div>
				</>],
				[],
				[t('date_dashboard.table.status'), 
					!data ? <Placeholder width={60} height={21} /> : (
						<Label 
							value={past ? t('date_dashboard.ended') : ((data.date.available_pdv || data.date.available_web) ? t('date_dashboard.published') : t('date_dashboard.not_published'))}
							color={past ? 'blue' : ((data.date.available_pdv || data.date.available_web) ? 'green' : 'yellow')}
						/>
					)
				],
				...((!data || (data.date.available_pdv || data.date.available_web)) ? [
					[t('date_dashboard.table.availability'), 
						!data ? <Placeholder width={30} height={21} /> : <>
							{data.date.available_pdv && <Label iconName='cash-register' value={'PDV'} />}
							{data.date.available_web && <Label iconName='globe' value={'Web'} style={{ marginLeft: 5 }} />}
						</>
					]
			 	] : []),
			]}
		/>
		{(!data || !!data?.summary) && <>
			<Groupper className='summary' defaultStyle={false} fitted title={t('dashboard.summary.title')}>
				{!data || (data.summary.money!==null && data.summary.average_sale!==null) ? (
					<div className="contents">
						<div className="row countdown">
							<Stat value={(
								data ? `$${addCommas(data.summary.money, false, true)}` : <Placeholder height={36} />
							)} label={t('dashboard.summary.income')} />
						</div>
						<div className="row">
							<Stat value={(
								data ? addCommas(data.summary.tickets, false, true) : <Placeholder height={36} />
							)} label={t('dashboard.summary.tickets')} />
							<Stat value={(
								data ? `$${addCommas(data.summary.average_sale)}` : <Placeholder height={36} />
							)} label={t('dashboard.summary.average')} />
						</div>
					</div>
				) : (
					<div className="contents">
						<div className="row countdown">
							<Stat value={addCommas(data.summary.tickets, false, true)} label={t('dashboard.summary.tickets')} />
						</div>
					</div>
				)}
			</Groupper>
			{!!data?.summary && (
				<div className="timestamp">{t('dashboard.timestamp')}: {moment.unix(data.summary.date).format('DD/MM/YYYY HH:mm:ss')}</div>
			)}
		</>}
	</div>
}

export default DateStatsDashboard;